import React from 'react';
import { PageHeader } from '../components/layout/PageHeader';
import { CtaBand } from '../components/ui/CtaBand';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Button } from '../components/ui/Button';
import { circles } from '../data/circles';
import { Voices } from '../components/home/Voices';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useBrand } from '../contexts/BrandContext';
export function Circles() {
  const {
    brand
  } = useBrand();
  useDocumentMeta({
    title: `Circles — ${brand.name}`,
    description: 'Small facilitated circles by stage, an overnight thread, and monthly clinician Q&As. Cameras optional. Included with membership.'
  });
  return <main className="w-full bg-cream" data-id="element-537" data-mp="3o1a7bnboxhhh">
      <PageHeader eyebrow="Circles" title="A room, not an audience." intro="Small groups, facilitated by a real person, meeting at a real time. Cameras are optional and most people keep them off. Included with membership, and the overnight thread is open to anyone." data-id="element-538" data-mp="3o4ei53jnvbh4" />

      <section className="bg-cream" data-id="element-539" data-mp="mastqbstz8n">
        <div className="mx-auto max-w-shell px-6 pb-20 pt-10 md:px-8 md:pb-28 md:pt-14" data-id="element-540" data-mp="3rriyp2cgebe9">
          <ul className="grid gap-8 lg:grid-cols-2" data-id="element-541" data-mp="9lnsjra4f9g6">
            {circles.map(circle => {
            const isThread = circle.seatsTotal === 0;
            const nearlyFull = !isThread && circle.seatsLeft <= 3;
            return <li key={circle.slug} className="flex" data-id="element-542" data-mp="jhr23i7ayad5">
                  <article className="flex w-full flex-col rounded-brand border border-ink/15 bg-cream-light p-8" data-id="element-543" data-mp="ekqzjh9nvpqs">
                    <div className="flex items-start justify-between gap-6" data-id="element-544" data-mp="sakm1sxp1xxc">
                      <p className="text-[0.6875rem] uppercase tracking-label text-brick" data-id="element-545" data-mp="o9imfazv7juu">
                        {circle.kind}
                      </p>
                      {isThread ? <p className="whitespace-nowrap text-sm text-brick" data-id="element-546" data-mp="xayxzm2f1s0n">Open now</p> : <p className={`whitespace-nowrap text-sm ${nearlyFull ? 'text-brick' : 'text-ink-soft'}`} data-id="element-547" data-mp="3r3hsmkgvz2eu">
                          {circle.seatsLeft} of {circle.seatsTotal} seats left
                        </p>}
                    </div>

                    <h2 className="mt-4 font-serif text-[1.75rem] leading-snug text-ink" data-id="element-548" data-mp="do29sr89lv3r">
                      {circle.name}
                    </h2>
                    <p className="mt-4 leading-relaxed text-ink-soft" data-id="element-549" data-mp="ilbxo2g09yph">{circle.description}</p>

                    <dl className="rule mt-auto grid gap-5 pt-6 sm:grid-cols-2" data-id="element-550" data-mp="mp04giwtcq9o">
                      <div data-id="element-551" data-mp="rmieydqhbdjs">
                        <dt className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-552" data-mp="rms7a7yzxwsn">
                          When
                        </dt>
                        <dd className="mt-1.5 text-[0.9375rem] text-ink" data-id="element-553" data-mp="2te4zbwfv8a7a">{circle.cadence}</dd>
                        <dd className="mt-1 text-sm text-brick" data-id="element-554" data-mp="2lvxf1bl7myiy">Next: {circle.nextSession}</dd>
                      </div>
                      <div data-id="element-555" data-mp="2h3x6iur2qxar">
                        <dt className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-556" data-mp="2h2m34w1jjrk6">
                          Held by
                        </dt>
                        <dd className="mt-1.5 text-[0.9375rem] text-ink" data-id="element-557" data-mp="277kf63j04vj6">{circle.host}</dd>
                        <dd className="mt-1 text-sm text-ink-soft" data-id="element-558" data-mp="34ujgirkn2rne">{circle.hostRole}</dd>
                      </div>
                    </dl>
                  </article>
                </li>;
          })}
          </ul>
        </div>
      </section>

      <section className="bg-cream-deep" data-id="element-559" data-mp="2uxy8ryh8j2fv">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-560" data-mp="2qv9xlk51tjfq">
          <div className="grid gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20" data-id="element-561" data-mp="1jgos1jphqoi9">
            <SectionHeading eyebrow="House rules" title="Small on purpose, so it stays a room." intro="Circles are capped at twelve. No recordings, ever. No advice-giving unless it is asked for. Nothing said in a circle is shared with your clinician unless you ask us to pass it on." data-id="element-562" data-mp="1jelri5qppcza" />
            <ul className="flex flex-col" data-id="element-563" data-mp="2temuhhu2v27a">
              {['Cameras optional — most people keep them off, especially at first.', 'You can attend and say nothing. Many women do for weeks.', 'No recordings, no transcripts, no summaries sent afterwards.', 'Facilitated by a named person, not moderated by an algorithm.', 'Leave a circle at any time without explaining why.'].map(rule => <li key={rule} className="rule py-4 leading-relaxed text-ink first:border-t-0 first:pt-0" data-id="element-564" data-mp="3h2wmj1hvwm6v">
                  {rule}
                </li>)}
            </ul>
          </div>
          <Button to="/pricing" variant="secondary" className="mt-12" data-id="element-565" data-mp="25osit3lhvjom">
            Circles are included with membership
          </Button>
        </div>
      </section>

      <Voices data-id="element-566" data-mp="1y6kw6c3d9vx4" />

      <CtaBand title="You don’t have to talk to be in the room." intro="Membership includes every circle and the clinician Q&As. The overnight thread is open to anyone, member or not — because 3am does not check your account status." data-id="element-567" data-mp="1t5x7vy6ivt6l" />
    </main>;
}